import { type ApiErrorPayload, toApiErrorPayload } from "@/utils/apiErrorPayload";

/** rejectWithValue payload / Error / AxiosError のいずれからも ApiErrorPayload を作る。 */
function normalizeToPayload(error: unknown): ApiErrorPayload {
  if (error && typeof error === "object" && !(error instanceof Error)) {
    const e = error as Record<string, unknown>;
    // 1) 既に ApiErrorPayload
    if ("errorType" in e) return e as unknown as ApiErrorPayload;
    // 2) 生レスポンス body { status, message, field, code } (AxiosError ではない)
    if (!("response" in e) && ("field" in e || "message" in e || "code" in e)) {
      return {
        errorType: "server",
        message: typeof e.message === "string" ? e.message : "",
        field: typeof e.field === "string" ? e.field : undefined,
        code: typeof e.code === "string" ? e.code : undefined,
        status: typeof e.status === "number" ? e.status : undefined,
      };
    }
  }
  // 3) Error / AxiosError / その他
  return toApiErrorPayload(error);
}

export interface ApplyApiErrorOptions {
  /** react-hook-form の setError。フィールド単位エラーをその input 下に表示する。 */
  setError?: (
    name: any,
    error: { type?: string; message: string },
  ) => void;
  /** react-hook-form の setFocus (任意)。エラー input にフォーカスする。 */
  setFocus?: (name: any) => void;
  /**
   * BE のフィールド名 → フォームのフィールド名 の対応。
   * 名前がズレる場合に指定 (例: { landmark_name: "name" })。未指定はそのまま使う。
   */
  fieldMap?: Record<string, string>;
  /**
   * フィールド単位でないエラー (field 無し / 500 / 通信失敗) のときの表示。
   * 通常はトーストを渡す: `(p) => showToast({ type: "error", message: p.message })`
   */
  onFallback?: (payload: ApiErrorPayload) => void;
}

/**
 * API エラーを振り分ける共通ヘルパー。
 * - `field` を持つバリデーションエラー → 該当 input 下にインライン表示 (+ focus)
 * - それ以外 (field 無し / 500 / 通信失敗) → onFallback (通常はトースト)
 *
 * `error` は Error / AxiosError / createAsyncThunk の rejectWithValue payload
 * (ApiErrorPayload) のいずれも受け取れる。
 *
 * @returns フィールド単位エラーとしてインライン表示したら true
 */
export function applyApiError(
  error: unknown,
  options: ApplyApiErrorOptions = {},
): boolean {
  const { setError, setFocus, fieldMap, onFallback } = options;

  // rejectWithValue の payload はスライスによって形が揺れる:
  //   1) ApiErrorPayload (errorType を持つ) … toApiErrorPayload を通したもの
  //   2) 生レスポンス body { status, message, field, code } … error.response.data をそのまま入れたもの
  //   3) Error / AxiosError … toApiErrorPayload で正規化
  const payload: ApiErrorPayload = normalizeToPayload(error);

  if (payload.field && setError) {
    const formField = fieldMap?.[payload.field] ?? payload.field;
    setError(formField, { type: "server", message: payload.message });
    setFocus?.(formField);
    return true;
  }

  onFallback?.(payload);
  return false;
}
